import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { listenToTripTrails } from '../services/firebase';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const CENTER = {
  lat: Number(process.env.REACT_APP_MAP_CENTER_LAT) || 13.6549,
  lng: Number(process.env.REACT_APP_MAP_CENTER_LNG) || 78.9471,
};

function loadMaps() {
  if (window.google?.maps) return Promise.resolve();
  if (!MAPS_KEY) return Promise.reject(new Error('missing_maps_key'));
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-mt-maps]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}`;
    s.async = true;
    s.dataset.mtMaps = '1';
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function haversineM(p1, p2) {
  const R = 6371000;
  const dLat = ((p2.lat - p1.lat) * Math.PI) / 180;
  const dLng = ((p2.lng - p1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((p1.lat * Math.PI) / 180) *
      Math.cos((p2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function RoadTraces({ drivers = [], rides = [] }) {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const polylinesRef = useRef([]);
  const [tripTrails, setTripTrails] = useState([]);
  const [ready, setReady] = useState(false);
  const [mapType, setMapType] = useState('roadmap'); // 'roadmap' | 'hybrid' | 'satellite'
  const [filterDays, setFilterDays] = useState('all'); // 'today' | '7days' | 'all'
  const [selectedTrail, setSelectedTrail] = useState(null);

  // Listen to tripTrails collection
  useEffect(() => {
    const unsub = listenToTripTrails(setTripTrails);
    return () => unsub?.();
  }, []);

  // Also harvest any trails stored on the completed rides collection
  const allTrails = useMemo(() => {
    const combined = new Map();

    // From dedicated tripTrails collection
    tripTrails.forEach((t) => {
      if (t.points && t.points.length > 1) {
        combined.set(t.id || t.rideId, {
          id: t.id || t.rideId,
          driverId: t.driverId,
          points: t.points,
          createdAt: t.createdAt?.toDate ? t.createdAt.toDate() : new Date(t.createdAt || Date.now()),
        });
      }
    });

    // From rides collection
    rides.forEach((r) => {
      if (Array.isArray(r.routeTrail) && r.routeTrail.length > 1 && !combined.has(r.id)) {
        combined.set(r.id, {
          id: r.id,
          driverId: r.driverId,
          points: r.routeTrail,
          createdAt: r.completedAt?.toDate ? r.completedAt.toDate() : new Date(r.completedAt || r.updatedAt || Date.now()),
        });
      }
    });

    return Array.from(combined.values());
  }, [tripTrails, rides]);

  // Filtered trails
  const filteredTrails = useMemo(() => {
    const now = Date.now();
    return allTrails.filter((trail) => {
      if (filterDays === 'today') {
        const diff = now - trail.createdAt.getTime();
        return diff <= 24 * 60 * 60 * 1000;
      }
      if (filterDays === '7days') {
        const diff = now - trail.createdAt.getTime();
        return diff <= 7 * 24 * 60 * 60 * 1000;
      }
      return true;
    });
  }, [allTrails, filterDays]);

  // Compute metrics
  const stats = useMemo(() => {
    let totalPoints = 0;
    let totalDistM = 0;
    const uniqueDrivers = new Set();

    filteredTrails.forEach((t) => {
      totalPoints += t.points.length;
      if (t.driverId) uniqueDrivers.add(t.driverId);
      for (let i = 1; i < t.points.length; i++) {
        totalDistM += haversineM(t.points[i - 1], t.points[i]);
      }
    });

    return {
      tracesCount: filteredTrails.length,
      totalPoints,
      totalKm: (totalDistM / 1000).toFixed(1),
      driversCount: uniqueDrivers.size,
    };
  }, [filteredTrails]);

  // Initialize Map
  useEffect(() => {
    let cancelled = false;
    loadMaps()
      .then(() => {
        if (cancelled || !mapRef.current || mapInst.current) return;
        mapInst.current = new window.google.maps.Map(mapRef.current, {
          center: CENTER,
          zoom: 14,
          mapTypeId: mapType,
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          styles: [
            { elementType: 'geometry', stylers: [{ color: '#131b26' }] },
            { elementType: 'labels.text.fill', stylers: [{ color: '#9baec8' }] },
            { elementType: 'labels.text.stroke', stylers: [{ color: '#131b26' }] },
            { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#27384c' }] },
            { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3b5370' }] },
            { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#090e14' }] },
          ],
        });
        setReady(true);
      })
      .catch((e) => console.error('Maps error:', e));
    return () => { cancelled = true; };
  }, []);

  // Update map type
  useEffect(() => {
    if (mapInst.current) {
      mapInst.current.setMapTypeId(mapType);
    }
  }, [mapType]);

function segmentizeTrail(points, maxGapM = 80, maxSpeedKmh = 65) {
  if (!Array.isArray(points) || points.length < 2) return [];
  const segments = [];
  let currentSegment = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const distM = haversineM(prev, curr);
    const dtSec = Math.abs((curr.t || 0) - (prev.t || 0)) / 1000;
    const speedKmh = dtSec > 0 ? (distM / dtSec) * 3.6 : 0;

    // Discard teleport spikes / large jumps (>80m gap or >65 km/h for auto)
    const isJump = distM > maxGapM || (dtSec > 0 && speedKmh > maxSpeedKmh);

    if (isJump) {
      if (currentSegment.length >= 2) {
        segments.push(currentSegment);
      }
      currentSegment = [curr];
    } else {
      currentSegment.push(curr);
    }
  }

  if (currentSegment.length >= 2) {
    segments.push(currentSegment);
  }
  return segments;
}

  // Draw Polylines for Traces with Intelligent Segment Splitting
  useEffect(() => {
    if (!ready || !mapInst.current || !window.google?.maps) return;

    // Clear old lines
    polylinesRef.current.forEach((line) => line.setMap(null));
    polylinesRef.current = [];

    // Draw each trail split into smooth continuous road segments
    filteredTrails.forEach((trail) => {
      const segments = segmentizeTrail(trail.points, 80, 65);

      segments.forEach((seg) => {
        const path = seg.map((p) => ({ lat: p.lat, lng: p.lng }));

        // Vivid luminous emerald lines along real road travel
        const polyline = new window.google.maps.Polyline({
          path,
          geodesic: true,
          strokeColor: '#00ff9d',
          strokeOpacity: 0.9,
          strokeWeight: 4,
          map: mapInst.current,
        });

        polyline.addListener('click', () => {
          setSelectedTrail(trail);
        });

        polylinesRef.current.push(polyline);
      });
    });
  }, [ready, filteredTrails]);

  // Export GPX 1.1 for OpenStreetMap
  const handleExportGpx = () => {
    if (filteredTrails.length === 0) {
      toast.error('No GPS traces to export yet.');
      return;
    }

    let gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="MyTownly Rides Piler Mapper" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>MyTownly Piler Road Traces</name>
    <desc>Crowdsourced auto-rickshaw road traces from Piler town</desc>
    <time>${new Date().toISOString()}</time>
  </metadata>
`;

    filteredTrails.forEach((trail) => {
      const segments = segmentizeTrail(trail.points, 80, 65);
      if (segments.length === 0) return;
      gpx += `  <trk>\n    <name>Trip ${trail.id.slice(0, 8)}</name>\n`;
      segments.forEach((seg) => {
        gpx += `    <trkseg>\n`;
        seg.forEach((pt) => {
          const timeIso = pt.t ? new Date(pt.t).toISOString() : new Date().toISOString();
          gpx += `      <trkpt lat="${pt.lat}" lon="${pt.lng}"><time>${timeIso}</time></trkpt>\n`;
        });
        gpx += `    </trkseg>\n`;
      });
      gpx += `  </trk>\n`;
    });

    gpx += `</gpx>`;

    const blob = new Blob([gpx], { type: 'application/gpx+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `piler_road_traces_${Date.now()}.gpx`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Clean GPX downloaded! Ready for OpenStreetMap editor.');
  };

  // Export GeoJSON for GIS / Mapbox
  const handleExportGeoJson = () => {
    if (filteredTrails.length === 0) {
      toast.error('No GPS traces to export yet.');
      return;
    }

    const geoJson = {
      type: 'FeatureCollection',
      features: filteredTrails.flatMap((trail) => {
        const segments = segmentizeTrail(trail.points, 80, 65);
        return segments.map((seg, sIdx) => ({
          type: 'Feature',
          properties: {
            rideId: trail.id,
            driverId: trail.driverId,
            segmentIndex: sIdx,
            pointCount: seg.length,
            recordedAt: trail.createdAt.toISOString(),
          },
          geometry: {
            type: 'LineString',
            coordinates: seg.map((p) => [p.lng, p.lat]),
          },
        }));
      }),
    };

    const blob = new Blob([JSON.stringify(geoJson, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `piler_roads_${Date.now()}.geojson`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('GeoJSON downloaded!');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', gap: 16 }}>
      {/* Top Stats Banner */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        <div style={{ background: '#1e293b', padding: '14px 18px', borderRadius: 12, border: '1px solid #334155' }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, display: 'block' }}>🛣️ Mapped Road Trips</span>
          <strong style={{ fontSize: 24, color: '#f8fafc', fontWeight: 800 }}>{stats.tracesCount}</strong>
          <small style={{ color: '#00ff9d', marginLeft: 8 }}>Traces active</small>
        </div>

        <div style={{ background: '#1e293b', padding: '14px 18px', borderRadius: 12, border: '1px solid #334155' }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, display: 'block' }}>📍 GPS Points Logged</span>
          <strong style={{ fontSize: 24, color: '#f8fafc', fontWeight: 800 }}>{stats.totalPoints.toLocaleString()}</strong>
          <small style={{ color: '#38bdf8', marginLeft: 8 }}>Coordinates</small>
        </div>

        <div style={{ background: '#1e293b', padding: '14px 18px', borderRadius: 12, border: '1px solid #334155' }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, display: 'block' }}>📏 Town Coverage Mapped</span>
          <strong style={{ fontSize: 24, color: '#f8fafc', fontWeight: 800 }}>{stats.totalKm} km</strong>
          <small style={{ color: '#fbbf24', marginLeft: 8 }}>Total Driven</small>
        </div>

        <div style={{ background: '#1e293b', padding: '14px 18px', borderRadius: 12, border: '1px solid #334155' }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, display: 'block' }}>👨‍✈️ Contributing Annas</span>
          <strong style={{ fontSize: 24, color: '#f8fafc', fontWeight: 800 }}>{stats.driversCount}</strong>
          <small style={{ color: '#a78bfa', marginLeft: 8 }}>Active Mappers</small>
        </div>
      </div>

      {/* Control Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: '#1e293b', padding: '10px 16px', borderRadius: 12, border: '1px solid #334155' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>Filter:</span>
          <button
            type="button"
            onClick={() => setFilterDays('today')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: filterDays === 'today' ? '#00A86B' : '#334155', color: '#fff', border: 'none',
            }}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setFilterDays('7days')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: filterDays === '7days' ? '#00A86B' : '#334155', color: '#fff', border: 'none',
            }}
          >
            Last 7 Days
          </button>
          <button
            type="button"
            onClick={() => setFilterDays('all')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: filterDays === 'all' ? '#00A86B' : '#334155', color: '#fff', border: 'none',
            }}
          >
            All Time ({allTrails.length})
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setMapType(mapType === 'roadmap' ? 'hybrid' : 'roadmap')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: '#334155', color: '#f8fafc', border: '1px solid #475569',
            }}
          >
            {mapType === 'roadmap' ? '🛰️ Satellite View' : '🗺️ Dark Road Map'}
          </button>

          <button
            type="button"
            onClick={handleExportGpx}
            style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer',
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', color: '#fff', border: 'none',
            }}
          >
            📥 Export GPX (OpenStreetMap)
          </button>

          <button
            type="button"
            onClick={handleExportGeoJson}
            style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer',
              background: 'linear-gradient(135deg, #00A86B 0%, #007a4d 100%)', color: '#fff', border: 'none',
            }}
          >
            📥 Export GeoJSON
          </button>
        </div>
      </div>

      {/* Main Map Canvas */}
      <div style={{ position: 'relative', flex: 1, minHeight: 400, borderRadius: 14, overflow: 'hidden', border: '1px solid #334155' }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

        {/* Selected Trail Overlay */}
        {selectedTrail && (
          <div style={{
            position: 'absolute', bottom: 20, left: 20, background: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid #38bdf8', padding: '12px 18px', borderRadius: 12, color: '#fff',
            maxWidth: 320, backdropFilter: 'blur(8px)', zIndex: 10,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <strong style={{ fontSize: 14, color: '#38bdf8' }}>Selected Road Trace</strong>
              <button
                type="button"
                onClick={() => setSelectedTrail(null)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 16 }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 12, color: '#cbd5e1' }}>
              <div>Trip ID: <code>{selectedTrail.id.slice(0, 10)}...</code></div>
              <div>Points: <strong>{selectedTrail.points.length} GPS pings</strong></div>
              <div>Recorded: {selectedTrail.createdAt.toLocaleDateString()} {selectedTrail.createdAt.toLocaleTimeString()}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
